import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { createRole, updateRole } from "../../../api/settings/roleManager";
import { toast } from "react-toastify";
import { AnimatePresence, motion } from "framer-motion";
import { getUserTypes } from "../../../api/settings/user_types";

const RoleCreateDrawer = ({ open, onOpenChange, onRefresh, data }) => {
  const [form, setForm] = useState({ code: "", name: "", userType: "", description: "" });
  const [userTypes, setUserTypes] = useState([]);   // [{id, name}]
  const [loadingUT, setLoadingUT] = useState(false);
  const [errorUT, setErrorUT] = useState("");
  const isEdit = !!data;

  // Ẩn bottom nav khi mở drawer
  useEffect(() => {
    const nav = document.getElementById("bottom-nav");
    if (!nav) return;
  
    if (open) {
      nav.style.display = "none";
    } else {
      nav.style.display = "";
    }
  
    return () => {
      nav.style.display = "";
    };
  }, [open]);

  // tạo form state với các trường cần thiết
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoadingUT(true);
        const list = await getUserTypes();
        if (!mounted) return;
        const normalized = (list || []).map(x => ({
          id: x.id ?? x.code ?? x.value,
          name: x.name ?? x.label ?? String(x.id ?? x.code ?? x.value)
        }));
        setUserTypes(normalized);
      } catch {
        setErrorUT("Không tải được danh sách User type");
      } finally {
        setLoadingUT(false);
      }
    })();
    return () => { mounted = false; };
  }, []);


  // Khi có dữ liệu từ props, cập nhật form state
  useEffect(() => {
    if (data) {
      setForm({
        code: data.id || "",                  // Gán id vào code (FE hiển thị)
        name: data.name || "",
        userType: data.user_type_id || "",    // Mapping đúng field backend
        description: data.description || "",
      });
    } else {
      setForm({ code: "", name: "", userType: "", description: "" });
    }
  }, [data]);

  const handleSave = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      toast.error("Please enter required fields");
      return;
    }
    try {
      if (isEdit) await updateRole(data.id, form);
      else await createRole({
        id: form.code.trim(), // FE đang có trường "ID" nhập tay
        name: form.name.trim(),
        userType: form.userType || null,
        description: form.description || null
      });
      toast.success("Saved successfully");
      onRefresh?.();
      onOpenChange(false);
    } catch (err) {
      toast.error("Error while saving role");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ duration: 0.3 }}
          className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-lg border-l z-50"
        >
          <div className="flex justify-between items-center px-4 py-3 border-b">
            <h2 className="text-lg font-semibold">{isEdit ? "Update role" : "New role"}</h2>
            <button onClick={() => onOpenChange(false)}><X size={20} /></button>
          </div>

          <form className="px-6 py-4 space-y-4 overflow-y-auto h-[calc(100%-140px)]">
            <div>
              <label className="text-sm font-medium block mb-1">
                ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Ex: admin, teacher"
                className="w-full border p-2 rounded"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                disabled={isEdit}
              />
            </div>

            <div>
              <label className="text-sm font-medium block mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Ex: Teacher, Admin"
                className="w-full border p-2 rounded"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>

            <div>
              <label className="text-sm font-medium block mb-1">User type</label>
              <div className="relative">
                <select
                  className="w-full border rounded px-3 py-2 appearance-none bg-white"
                  value={form.userType || ""}
                  onChange={(e) => setForm({ ...form, userType: e.target.value })}
                  disabled={loadingUT || !!errorUT}
                >
                  <option value="">-- Select user type --</option>
                  {userTypes.map((ut) => (
                    <option key={ut.id} value={ut.id}>{ut.name}</option>
                  ))}
                </select>
                {loadingUT && (
                  <span className="absolute right-3 top-2.5 text-xs text-gray-500">Loading...</span>
                )}
                {errorUT && (
                  <span className="block mt-1 text-xs text-red-600">{errorUT}</span>
                )}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium block mb-1">Description</label>
              <textarea
                rows={4}
                placeholder="Ex: Role for managing student accounts"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full border rounded px-3 py-2"
              />
            </div>
          </form>

          <div className="flex justify-end gap-3 px-4 py-4 border-t">
            <button onClick={() => onOpenChange(false)} className="border border-gray-300 px-4 py-2 rounded hover:bg-gray-50">
              Cancel
            </button>
            <button onClick={handleSave} className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700">
              Save
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RoleCreateDrawer;
